import React, { useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { InputProps, Text } from "@ui-kitten/components";
import { FieldProps, getIn } from "formik";
import Input from "./Input";
import { UserFieldDetails } from "features/types";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import capitalize from "lodash/capitalize";

type TextInputProp = { userFieldType: UserFieldDetails["type"] } & FieldProps &
  InputProps;

export const formattedDate = (value: string) => {
  if (!value) return value;
  value = value.replace(/[^\d]/g, "");
  if (value.length <= 2) return value;
  if (value.length <= 4) {
    return `${value.slice(0, 2)}/${value.slice(2)}`;
  }
  return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
};

export const formattedPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const TextInput = (props: TextInputProp) => {
  const {
    field: { name, onBlur, onChange, value },
    form: { errors, touched, setFieldTouched },
    appearance = "default",
    label,
    secureTextEntry,
    // @ts-ignore
    innerRef,
    userFieldType = "STRING",
    status = "basic",
    ...inputProps
  } = props;

  const errorMsg = touched[name] && capitalize(getIn(errors, name));
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const timezone = tenantConfig?.timezone;

  // format birthdate and phone number fields when component loads
  useEffect(() => {
    if (name === "phone" && value) {
      onChange(name)(formattedPhoneNumber(value));
    }
  }, []);

  if (!timezone) return null;

  const handleChangeText = (text: string) => {
    if (name === "phone") {
      onChange(name)(formattedPhoneNumber(text));
    } else {
      onChange(name)(text);
    }
  };

  return (
    <View style={{ marginBottom: 15 }}>
      <Input
        ref={innerRef}
        value={value || ""}
        onChangeText={handleChangeText}
        onBlur={() => {
          setFieldTouched(name);
          onBlur(name);
        }}
        label={label}
        {...inputProps}
        style={[
          appearance !== "default" ? styles.textInput : {},
          inputProps.style,
        ]}
        secureTextEntry={secureTextEntry}
        textContentType="none"
        status={errorMsg ? "danger" : status}
        placeholder={
          userFieldType === "DATE" ? "MM/DD/YYYY" : inputProps.placeholder
        }
        keyboardType={
          userFieldType === "NUMBER" || name === "phone"
            ? "numeric"
            : inputProps.keyboardType
        }
        testID={name.toLowerCase()}
      ></Input>

      {errorMsg ? <Text style={styles.errorText}>{errorMsg}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  textInput: {
    backgroundColor: "transparent",
    borderWidth: 0,
    borderBottomWidth: 1,
    borderRadius: 0,
  },
  errorText: {
    color: "red",
    fontSize: 13,
    marginLeft: 5,
  },
  errorInput: {
    borderColor: "red",
  },
});

export default TextInput;
